// src/pages/Earnings.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMerchant } from '../context/MerchantContext';
import { listenToShopOrders, listenToMerchantPayouts, merchantLogout } from '../services/firebase';
import { attachReconnectingListener } from '../utils/liveListener';
import {
  computeMerchantBalance,
  todayDeliveredSubtotal,
  weekDeliveredSubtotal,
  merchantDayCollection,
  isCod,
  orderSubtotal,
  paymentBadge,
} from '../utils/orders';
import { formatDayLabel, todayKey, yesterdayKey, recentDayKeys } from '../utils/dates';

const NAV = [
  { label: '📦 Orders', path: '/' },
  { label: '💰 Earnings', path: '/earnings', active: true },
  { label: '🛒 Products', path: '/products' },
  { label: '⚙️ Settings', path: '/settings' },
];

const fmtMoney = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;

const fmtTime = (ts) => {
  const d = ts?.toDate?.();
  if (!d) return '—';
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

export default function Earnings() {
  const navigate = useNavigate();
  const { shop } = useMerchant();
  const [orders, setOrders] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(todayKey());

  useEffect(() => {
    if (!shop?.id) return;
    setLoading(true);
    const unsubOrders = attachReconnectingListener(() => listenToShopOrders(
      shop.id,
      (data) => { setOrders(data); setLoading(false); },
      () => setLoading(false),
    ));
    const unsubPayouts = listenToMerchantPayouts(shop.id, setPayouts);
    return () => { unsubOrders(); unsubPayouts(); };
  }, [shop?.id]);

  const dayCollection = useMemo(
    () => (shop?.id ? merchantDayCollection(orders, shop.id, selectedDay) : null),
    [orders, shop?.id, selectedDay],
  );

  const dayRows = useMemo(() => {
    if (!shop?.id) return [];
    return recentDayKeys(14).map((dayKey) => {
      const col = merchantDayCollection(orders, shop.id, dayKey);
      return { dayKey, ...col };
    }).filter((r) => r.deliveries > 0);
  }, [orders, shop?.id]);

  if (!shop) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--black)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: 'var(--gray)' }}>No shop linked</div>
      </div>
    );
  }

  const todaySubtotal = todayDeliveredSubtotal(orders, shop.id);
  const weekSubtotal = weekDeliveredSubtotal(orders, shop.id);
  const pendingBalance = computeMerchantBalance(orders, payouts, shop.id);
  const paidTotal = payouts
    .filter((p) => p.status === 'paid')
    .reduce((s, p) => s + (Number(p.amount) || 0), 0);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--black)', maxWidth: 900, margin: '0 auto' }}>
      <div style={{ background: 'var(--dark)', borderBottom: '1px solid var(--border)', padding: '0 24px', display: 'flex', alignItems: 'center', height: 60, gap: 16 }}>
        <div style={{ fontSize: 20 }}>🏪</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: 'var(--green)' }}>{shop.name}</div>
          <div style={{ fontSize: 11, color: 'var(--gray)' }}>Merchant Dashboard</div>
        </div>
        {NAV.map((n) => (
          <button key={n.label} onClick={() => navigate(n.path)} style={{
            padding: '7px 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
            background: n.active ? 'rgba(0,214,143,0.1)' : 'transparent',
            color: n.active ? 'var(--green)' : 'var(--gray)',
            fontFamily: 'DM Sans, sans-serif', fontSize: 13, fontWeight: 700,
          }}>{n.label}</button>
        ))}
        <button onClick={() => merchantLogout()} style={{ background: 'none', border: 'none', color: 'var(--gray)', cursor: 'pointer', fontSize: 13 }}>Logout</button>
      </div>

      <div style={{ padding: 24 }}>
        <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Earnings 💰</div>
        <div style={{ fontSize: 12, color: 'var(--gray)', marginBottom: 24, lineHeight: 1.6 }}>
          Earnings are based on item subtotals only. MyTownly delivery fees are collected by the platform.
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60 }}><div className="spinner" style={{ width: 32, height: 32 }} /></div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
              {[
                { label: 'Today (delivered)', value: fmtMoney(todaySubtotal), color: 'var(--green)' },
                { label: 'This week (delivered)', value: fmtMoney(weekSubtotal), color: 'var(--blue)' },
                { label: 'Pending balance', value: fmtMoney(pendingBalance), color: 'var(--orange)' },
                { label: 'Total paid out', value: fmtMoney(paidTotal), color: 'var(--gray)' },
              ].map((s) => (
                <div key={s.label} className="card" style={{ padding: 16, textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 900, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 4 }}>{s.label}</div>
                </div>
              ))}
            </div>

            <div className="card" style={{ padding: 20, marginBottom: 24 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 800 }}>Daily collections</div>
                  <div style={{ fontSize: 12, color: 'var(--gray)', marginTop: 4 }}>{formatDayLabel(selectedDay)}</div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => setSelectedDay(todayKey())}
                    style={{
                      padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700,
                      background: selectedDay === todayKey() ? 'var(--green)' : 'var(--card)',
                      color: selectedDay === todayKey() ? '#0a0a0a' : 'var(--gray)',
                    }}
                  >
                    Today
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedDay(yesterdayKey())}
                    style={{
                      padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', cursor: 'pointer', fontSize: 12, fontWeight: 700,
                      background: selectedDay === yesterdayKey() ? 'var(--green)' : 'transparent',
                      color: selectedDay === yesterdayKey() ? '#0a0a0a' : 'var(--gray)',
                    }}
                  >
                    Yesterday
                  </button>
                  <input
                    type="date"
                    value={selectedDay}
                    max={todayKey()}
                    onChange={(e) => e.target.value && setSelectedDay(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 8,
                      border: '1px solid var(--border)',
                      background: 'var(--dark)',
                      color: 'var(--white)',
                      fontFamily: 'JetBrains Mono, monospace',
                      fontSize: 12,
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
                <div style={{ padding: 12, background: 'rgba(255,193,7,0.08)', borderRadius: 8, border: '1px solid rgba(255,193,7,0.2)' }}>
                  <div style={{ fontSize: 10, color: 'var(--gray)', fontFamily: 'JetBrains Mono' }}>COD (subtotal)</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#ffc107' }}>{fmtMoney(dayCollection?.cod.subtotal)}</div>
                  <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 4 }}>{dayCollection?.cod.count ?? 0} orders · rider collects cash</div>
                </div>
                <div style={{ padding: 12, background: 'rgba(59,130,246,0.08)', borderRadius: 8, border: '1px solid rgba(59,130,246,0.2)' }}>
                  <div style={{ fontSize: 10, color: 'var(--gray)', fontFamily: 'JetBrains Mono' }}>ONLINE (subtotal)</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--blue)' }}>{fmtMoney(dayCollection?.online.subtotal)}</div>
                  <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 4 }}>{dayCollection?.online.count ?? 0} orders · paid in app</div>
                </div>
                <div style={{ padding: 12, background: 'rgba(0,214,143,0.08)', borderRadius: 8, border: '1px solid rgba(0,214,143,0.2)' }}>
                  <div style={{ fontSize: 10, color: 'var(--gray)', fontFamily: 'JetBrains Mono' }}>YOUR TOTAL</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--green)' }}>{fmtMoney(dayCollection?.totalSubtotal)}</div>
                  <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 4 }}>{dayCollection?.deliveries ?? 0} delivered</div>
                </div>
              </div>

              {(dayCollection?.orders?.length ?? 0) === 0 ? (
                <div style={{ textAlign: 'center', padding: 24, color: 'var(--gray)', fontSize: 13 }}>
                  No delivered orders on this day
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {dayCollection.orders.map((o) => {
                    const badge = paymentBadge(o);
                    return (
                      <div
                        key={o.id}
                        style={{
                          padding: 12,
                          background: 'var(--dark)',
                          borderRadius: 8,
                          borderLeft: `3px solid ${isCod(o) ? '#ffc107' : 'var(--blue)'}`,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 12,
                        }}
                      >
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700 }}>
                            #{o.id.slice(0, 8)} · {fmtTime(o.deliveredAt || o.updatedAt)}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 2 }}>
                            {(o.items || []).slice(0, 2).map((i) => i.name).join(', ')}
                            {(o.items?.length ?? 0) > 2 ? ` +${o.items.length - 2}` : ''}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontWeight: 800, color: 'var(--green)' }}>{fmtMoney(orderSubtotal(o))}</div>
                          <span className={`badge badge-${isCod(o) ? 'pending' : 'confirmed'}`} style={{ fontSize: 10, marginTop: 4 }}>
                            {badge.icon} {badge.label}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {dayRows.length > 0 && (
              <div className="card" style={{ padding: 16, marginBottom: 24, overflowX: 'auto' }}>
                <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 12 }}>Last 14 days</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ color: 'var(--gray)', textAlign: 'left' }}>
                      <th style={{ padding: '8px 10px', fontWeight: 600 }}>DATE</th>
                      <th style={{ padding: '8px 10px', fontWeight: 600 }}>ORDERS</th>
                      <th style={{ padding: '8px 10px', fontWeight: 600, color: '#ffc107' }}>COD</th>
                      <th style={{ padding: '8px 10px', fontWeight: 600, color: 'var(--blue)' }}>ONLINE</th>
                      <th style={{ padding: '8px 10px', fontWeight: 600, color: 'var(--green)' }}>TOTAL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dayRows.map((row) => (
                      <tr
                        key={row.dayKey}
                        onClick={() => setSelectedDay(row.dayKey)}
                        style={{
                          cursor: 'pointer',
                          background: row.dayKey === selectedDay ? 'rgba(0,214,143,0.08)' : 'transparent',
                        }}
                      >
                        <td style={{ padding: '10px', fontWeight: row.dayKey === selectedDay ? 700 : 500 }}>
                          {formatDayLabel(row.dayKey)}
                        </td>
                        <td style={{ padding: '10px', fontFamily: 'JetBrains Mono' }}>{row.deliveries}</td>
                        <td style={{ padding: '10px', fontFamily: 'JetBrains Mono', color: '#ffc107' }}>{fmtMoney(row.cod.subtotal)}</td>
                        <td style={{ padding: '10px', fontFamily: 'JetBrains Mono', color: 'var(--blue)' }}>{fmtMoney(row.online.subtotal)}</td>
                        <td style={{ padding: '10px', fontFamily: 'JetBrains Mono', fontWeight: 700, color: 'var(--green)' }}>{fmtMoney(row.totalSubtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="card" style={{ padding: 16, marginBottom: 24, background: 'rgba(0,214,143,0.06)', border: '1px solid rgba(0,214,143,0.2)' }}>
              <div style={{ fontSize: 12, color: 'var(--gray)', lineHeight: 1.6 }}>
                <strong style={{ color: 'var(--green)' }}>Pending balance</strong> = all delivered order subtotals minus payouts marked paid or pending by MyTownly admin.
                Payouts are generated daily and transferred after admin review.
              </div>
            </div>

            <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 12 }}>Payout history</div>
            {payouts.length === 0 ? (
              <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--gray)' }}>
                No payouts yet — earnings appear here after admin generates daily payouts.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {payouts.map((p) => (
                  <div key={p.id} className="card" style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 800 }}>₹{p.amount}</div>
                      <div style={{ fontSize: 12, color: 'var(--gray)', marginTop: 2 }}>
                        {p.period ? formatDayLabel(p.period) : '—'}
                        {p.orderCount != null ? ` • ${p.orderCount} orders` : ''}
                      </div>
                      {p.notes && <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 4 }}>{p.notes}</div>}
                    </div>
                    <span className={`badge badge-${p.status === 'paid' ? 'delivered' : 'pending'}`}>
                      {p.status === 'paid' ? '✓ Paid' : '⏳ Pending'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
